import React, { useState, useEffect } from 'react';
import {
  Video,
  Users,
  Clock,
  Calendar,
  Plus,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Phone,
  FileText,
  Play,
  Square,
  UserCheck,
  AlertTriangle,
  MessageSquare,
  Send,
} from 'lucide-react';
import { NEU } from '../neuStyles';
import PatientDropdown from '../components/PatientDropdown';

export default function TelemedicinePage({ user }) {
  const API = window.zignagenixAPI;
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({
    sessions_today: 0,
    avg_duration: 0,
    total_completed: 0,
    total_no_show: 0,
  });
  const [patients, setPatients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [waitingRoom, setWaitingRoom] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // New session form state
  const [newSessionForm, setNewSessionForm] = useState({
    patient_id: '',
    provider_id: '',
    scheduled_at: '',
    appointment_type: 'consultation',
    notes: '',
  });

  // New note state
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, sessionsData, patientsData, providersData, waitingData] =
        await Promise.all([
          API.getTelemedStats(),
          API.getTelemedSessions({}),
          API.getPatients(),
          API.getUsers(),
          API.getTelemedWaitingRoom(),
        ]);

      setStats(statsData);
      setSessions(sessionsData || []);
      setPatients(patientsData || []);
      setProviders(providersData || []);
      setWaitingRoom(waitingData || []);
    } catch (err) {
      console.error('Error loading telemedicine data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async () => {
    if (!newSessionForm.patient_id || !newSessionForm.provider_id || !newSessionForm.scheduled_at) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await API.createTelemedSession(newSessionForm);
      setNewSessionForm({
        patient_id: '',
        provider_id: '',
        scheduled_at: '',
        appointment_type: 'consultation',
        notes: '',
      });
      await loadData();
      setActiveTab('sessions');
    } catch (err) {
      console.error('Error creating session:', err);
      alert('Failed to create session');
    }
  };

  const handleStatusChange = async (sessionId, newStatus) => {
    try {
      await API.updateTelemedStatus(sessionId, newStatus);
      await loadData();
      if (selectedSession?.id === sessionId) {
        const updated = await API.getTelemedSession(sessionId);
        setSelectedSession(updated);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedSession) return;

    setAddingNote(true);
    try {
      await API.addTelemedNotes(selectedSession.id, newNote);
      setNewNote('');
      const updated = await API.getTelemedSession(selectedSession.id);
      setSelectedSession(updated);
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleCreateEncounter = async () => {
    if (!selectedSession) return;

    try {
      await API.createTelemedEncounter(selectedSession.id);
      alert('Encounter created successfully');
      const updated = await API.getTelemedSession(selectedSession.id);
      setSelectedSession(updated);
    } catch (err) {
      console.error('Error creating encounter:', err);
      alert('Failed to create encounter');
    }
  };

  const handleGenerateToken = async () => {
    if (!selectedSession) return;

    try {
      const tokenData = await API.generateTelemedToken(
        selectedSession.id,
        user.id || user.email
      );
      alert(`Token generated. Room: ${tokenData.room_name}`);
    } catch (err) {
      console.error('Error generating token:', err);
      alert('Failed to generate token');
    }
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      scheduled: { bg: '#f3f4f6', text: '#374151', icon: Calendar },
      checked_in: { bg: '#dbeafe', text: '#1e40af', icon: UserCheck },
      in_progress: { bg: '#dcfce7', text: '#15803d', icon: Play },
      completed: { bg: '#ccfbf1', text: '#0d9488', icon: CheckCircle2 },
      cancelled: { bg: '#fee2e2', text: '#991b1b', icon: XCircle },
      no_show: { bg: '#fed7aa', text: '#92400e', icon: AlertTriangle },
    };
    return styles[status] || styles.scheduled;
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleString();
  };

  // ========== DASHBOARD TAB ==========
  const DashboardTab = () => (
    <div style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '24px', color: '#1f2937' }}>
        Telemedicine Dashboard
      </h2>

      {/* Stats Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}
      >
        {[
          {
            label: 'Sessions Today',
            value: stats.sessions_today,
            icon: Video,
            color: '#2E8B98',
          },
          {
            label: 'Avg Duration (min)',
            value: Math.round(stats.avg_duration || 0),
            icon: Clock,
            color: '#2E8B98',
          },
          {
            label: 'Total Completed',
            value: stats.total_completed,
            icon: CheckCircle2,
            color: '#2E8B98',
          },
          { label: 'No-Shows', value: stats.total_no_show, icon: AlertTriangle, color: '#dc2626' },
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div
              key={idx}
              style={{
                ...NEU.card,
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '10px',
                  background: `linear-gradient(145deg, ${stat.color}20, ${stat.color}10)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon size={28} color={stat.color} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                  {stat.value}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Upcoming Sessions */}
      <div
        style={{
          ...NEU.card,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px', ...NEU.dividerTop }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Upcoming Sessions
          </h3>
        </div>
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {sessions
            .filter((s) => s.status === 'scheduled')
            .slice(0, 5)
            .map((session) => {
              const patientName =
                patients.find((p) => p.id === session.patient_id)?.name || 'Unknown Patient';
              const providerName =
                providers.find((p) => p.id === session.provider_id)?.name || 'Unknown Provider';
              return (
                <div
                  key={session.id}
                  style={{
                    padding: '16px 20px',
                    ...NEU.dividerTop,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      {patientName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      with {providerName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      {formatDateTime(session.scheduled_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedSession(session);
                      setActiveTab('sessions');
                    }}
                    style={{
                      padding: '8px 16px',
                      background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    View
                  </button>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );

  // ========== SESSIONS TAB ==========
  const SessionsTab = () => {
    const [filterStatus, setFilterStatus] = useState('all');
    const [showNewForm, setShowNewForm] = useState(false);

    const filteredSessions = sessions.filter((s) =>
      filterStatus === 'all' ? true : s.status === filterStatus
    );

    return (
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Telemedicine Sessions
          </h2>
          <button
            onClick={() => setShowNewForm(!showNewForm)}
            style={{
              padding: '10px 16px',
              background: 'linear-gradient(145deg, #2E8B98, #007a96)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Plus size={18} /> New Session
          </button>
        </div>

        {/* New Session Form */}
        {showNewForm && (
          <div
            style={{
              ...NEU.card,
              padding: '20px',
              marginBottom: '24px',
            }}
          >
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginTop: 0, marginBottom: '16px' }}>
              Create New Session
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px',
              }}
            >
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Patient
                </label>
                <PatientDropdown
                  patients={patients}
                  value={newSessionForm.patient_id}
                  onChange={val => setNewSessionForm({ ...newSessionForm, patient_id: val })}
                  placeholder="Select patient..."
                  idPrefix="telemed"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Provider
                </label>
                <select
                  value={newSessionForm.provider_id}
                  onChange={(e) =>
                    setNewSessionForm({ ...newSessionForm, provider_id: e.target.value })
                  }
                  style={{
                    ...NEU.input,
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Select provider...</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={newSessionForm.scheduled_at}
                  onChange={(e) =>
                    setNewSessionForm({ ...newSessionForm, scheduled_at: e.target.value })
                  }
                  style={{
                    ...NEU.input,
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Appointment Type
                </label>
                <select
                  value={newSessionForm.appointment_type}
                  onChange={(e) =>
                    setNewSessionForm({ ...newSessionForm, appointment_type: e.target.value })
                  }
                  style={{
                    ...NEU.input,
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                  }}
                >
                  <option value="consultation">Consultation</option>
                  <option value="followup">Follow-up</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Notes
              </label>
              <textarea
                value={newSessionForm.notes}
                onChange={(e) =>
                  setNewSessionForm({ ...newSessionForm, notes: e.target.value })
                }
                style={{
                  ...NEU.input,
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  minHeight: '80px',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleCreateSession}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Create Session
              </button>
              <button
                onClick={() => setShowNewForm(false)}
                style={{
                  ...NEU.btnGhost,
                  padding: '10px 16px',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Filter Buttons */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {['all', 'scheduled', 'checked_in', 'in_progress', 'completed', 'cancelled', 'no_show'].map(
            (status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                style={{
                  padding: '8px 14px',
                  background: filterStatus === status ? '#2E8B98' : '#f3f4f6',
                  color: filterStatus === status ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {status}
              </button>
            )
          )}
        </div>

        {/* Sessions List */}
        {selectedSession ? (
          <SessionDetailView session={selectedSession} onBack={() => setSelectedSession(null)} />
        ) : (
          <div
            style={{
              ...NEU.card,
              overflow: 'hidden',
            }}
          >
            {filteredSessions.length === 0 ? (
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontSize: '14px',
                }}
              >
                No sessions found
              </div>
            ) : (
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F8F9FA', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)' }}>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151',
                        }}
                      >
                        Patient
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151',
                        }}
                      >
                        Provider
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151',
                        }}
                      >
                        Scheduled
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151',
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151',
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.map((session) => {
                      const patientName =
                        patients.find((p) => p.id === session.patient_id)?.name ||
                        'Unknown';
                      const providerName =
                        providers.find((p) => p.id === session.provider_id)?.name ||
                        'Unknown';
                      const statusStyle = getStatusBadgeStyle(session.status);

                      return (
                        <tr
                          key={session.id}
                          style={{
                            ...NEU.dividerTop,
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                          }}
                        >
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#1f2937' }}>
                            {patientName}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#1f2937' }}>
                            {providerName}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>
                            {formatDateTime(session.scheduled_at)}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                background: statusStyle.bg,
                                color: statusStyle.text,
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500',
                                textTransform: 'capitalize',
                              }}
                            >
                              {session.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <button
                              onClick={() => setSelectedSession(session)}
                              style={{
                                padding: '6px 12px',
                                background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ========== SESSION DETAIL VIEW ==========
  const SessionDetailView = ({ session, onBack }) => {
    const patientName = patients.find((p) => p.id === session.patient_id)?.name || 'Unknown';
    const providerName = providers.find((p) => p.id === session.provider_id)?.name || 'Unknown';
    const statusStyle = getStatusBadgeStyle(session.status);

    const actionButtons = {
      scheduled: [
        { label: 'Check In', status: 'checked_in', icon: UserCheck },
        { label: 'Cancel', status: 'cancelled', icon: XCircle },
      ],
      checked_in: [
        { label: 'Start Session', status: 'in_progress', icon: Play },
        { label: 'No Show', status: 'no_show', icon: AlertTriangle },
      ],
      in_progress: [
        { label: 'End Session', status: 'completed', icon: Square },
      ],
      completed: [],
      cancelled: [],
      no_show: [],
    };

    const buttons = actionButtons[session.status] || [];

    return (
      <div>
        {/* Back Button */}
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            background: 'none',
            border: 'none',
            color: '#2E8B98',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px',
          }}
        >
          <ArrowLeft size={18} /> Back to Sessions
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Left Column */}
          <div>
            {/* Session Info Card */}
            <div
              style={{
                ...NEU.card,
                padding: '20px',
                marginBottom: '20px',
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginTop: 0, marginBottom: '16px' }}>
                Session Details
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    Patient
                  </div>
                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '600' }}>
                    {patientName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    Provider
                  </div>
                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '600' }}>
                    {providerName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    Scheduled Time
                  </div>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>
                    {formatDateTime(session.scheduled_at)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    Type
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#1f2937',
                      textTransform: 'capitalize',
                    }}
                  >
                    {session.appointment_type}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    Status
                  </div>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '6px 12px',
                      background: statusStyle.bg,
                      color: statusStyle.text,
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      textTransform: 'capitalize',
                      marginTop: '4px',
                    }}
                  >
                    {session.status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              {buttons.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {buttons.map((btn) => {
                    const Icon = btn.icon;
                    return (
                      <button
                        key={btn.status}
                        onClick={() => handleStatusChange(session.id, btn.status)}
                        style={{
                          padding: '10px 14px',
                          background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <Icon size={16} /> {btn.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Video Token Generation */}
            {(session.status === 'in_progress' || session.status === 'checked_in') && (
              <div
                style={{
                  background: '#F8F9FA',
                  border: 'none',
                  borderRadius: 12,
                  boxShadow: NEU.shadow.sm,
                  padding: '16px',
                  marginBottom: '20px',
                  borderLeft: '4px solid #2E8B98',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <Phone size={18} color='#2E8B98' />
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#2E8B98' }}>
                    Video Call
                  </div>
                </div>
                <button
                  onClick={handleGenerateToken}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Generate Video Token
                </button>
              </div>
            )}

            {/* Create Encounter */}
            {session.status === 'completed' && (
              <div
                style={{
                  background: '#F8F9FA',
                  border: 'none',
                  borderRadius: 12,
                  boxShadow: NEU.shadow.sm,
                  padding: '16px',
                  borderLeft: '4px solid #16a34a',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <FileText size={18} color='#16a34a' />
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a' }}>
                    Create Encounter
                  </div>
                </div>
                <button
                  onClick={handleCreateEncounter}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'linear-gradient(145deg, #16a34a, #15803d)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Create Encounter
                </button>
              </div>
            )}
          </div>

          {/* Right Column - Notes */}
          <div
            style={{
              ...NEU.card,
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginTop: 0, marginBottom: '16px' }}>
              Clinical Notes
            </h3>

            {/* Notes Timeline */}
            <div style={{ flex: 1, minHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
              {session.notes && session.notes.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {session.notes.map((note, idx) => (
                    <div
                      key={idx}
                      style={{
                        ...NEU.inset,
                        padding: '12px',
                        borderLeft: `3px solid ${NEU.teal}`,
                      }}
                    >
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        {new Date(note.created_at || Date.now()).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '13px', color: '#1f2937', lineHeight: '1.5' }}>
                        {note.text || note}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '13px' }}>
                  No notes yet
                </div>
              )}
            </div>

            {/* Add Note Form */}
            <div style={{ ...NEU.dividerTop, paddingTop: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                Add Note
              </label>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder='Enter clinical notes...'
                style={{
                  ...NEU.input,
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '13px',
                  minHeight: '60px',
                  marginBottom: '8px',
                  resize: 'none',
                }}
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || addingNote}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: newNote.trim() && !addingNote ? 'linear-gradient(145deg, #2E8B98, #007a96)' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: newNote.trim() && !addingNote ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <Send size={14} /> {addingNote ? 'Sending...' : 'Send Note'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ========== WAITING ROOM TAB ==========
  const WaitingRoomTab = () => {
    return (
      <div style={{ padding: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '24px', color: '#1f2937' }}>
          Waiting Room
        </h2>

        <div
          style={{
            ...NEU.card,
            overflow: 'hidden',
          }}
        >
          {waitingRoom.length === 0 ? (
            <div
              style={{
                padding: '60px 20px',
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: '16px',
              }}
            >
              <Users size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <div>No patients in waiting room</div>
            </div>
          ) : (
            <div style={{ padding: '20px' }}>
              {waitingRoom.map((session) => {
                const patientName =
                  patients.find((p) => p.id === session.patient_id)?.name || 'Unknown';
                const checkedInTime = new Date(session.checked_in_at || Date.now());
                const waitTime = Math.round((Date.now() - checkedInTime.getTime()) / 60000);

                return (
                  <div
                    key={session.id}
                    style={{
                      ...NEU.inset,
                      padding: '16px',
                      marginBottom: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                        {patientName}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                        Waiting for {waitTime} minute{waitTime !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => handleStatusChange(session.id, 'in_progress')}
                      style={{
                        padding: '10px 18px',
                        background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Play size={16} /> Start Session
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========== MAIN RENDER ==========
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: '#9ca3af',
          fontSize: '16px',
        }}
      >
        Loading telemedicine data...
      </div>
    );
  }

  return (
    <div style={{ background: NEU.bg, minHeight: '100vh' }}>
      {/* Tab Navigation */}
      <div
        style={{
          background: '#F8F9FA',
          borderBottom: 'none',
          boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)',
          display: 'flex',
          gap: '0',
        }}
      >
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Video },
          { id: 'sessions', label: 'Sessions', icon: Calendar },
          { id: 'waiting', label: 'Waiting Room', icon: Users },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedSession(null);
              }}
              style={{
                flex: 1,
                padding: '16px 20px',
                background: isActive ? '#f3f4f6' : 'white',
                color: isActive ? '#1f2937' : '#6b7280',
                border: 'none',
                borderBottom: isActive ? '3px solid #2E8B98' : 'none',
                fontSize: '14px',
                fontWeight: isActive ? '600' : '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <DashboardTab />}
      {activeTab === 'sessions' && <SessionsTab />}
      {activeTab === 'waiting' && <WaitingRoomTab />}
    </div>
  );
}
